// frontend/src/hooks/useWaypointTranslations.js
// Custom hook for managing waypoint translations

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import translationService from '../services/translationService';

/**
 * Custom hook to fetch and manage waypoint translations
 * @param {Object} displayData - The display data containing waypoints
 * @param {Object} entitySettings - Entity settings including multi-language flag
 * @param {Object} currentEntity - Current entity information
 * @returns {Object} Waypoint translations keyed by waypoint ID
 */
export const useWaypointTranslations = (displayData, entitySettings, currentEntity) => {
    const [waypointTranslations, setWaypointTranslations] = useState({});
    const { i18n } = useTranslation();

    useEffect(() => {
        const fetchWaypointTranslations = async () => {
            // Determine multi-language flag and entity identifiers
            const defaultLanguage = entitySettings?.entity_default_language || currentEntity?.entity_default_language || 'en';
            const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage;
            const entityId = currentEntity?.entityId || currentEntity?.id;

            if (!shouldTranslateContent || !entityId) {
                setWaypointTranslations({});
                return;
            }

            // Get all unique waypoint IDs from display data
            const waypointIds = new Set();
            if (displayData?.waypoints && Array.isArray(displayData.waypoints)) {
                displayData.waypoints.forEach(wp => {
                    if (wp.waypoint_id) {
                        waypointIds.add(wp.waypoint_id);
                    }
                });
            }

            if (waypointIds.size === 0) {
                return;
            }

            const translations = {};
            const userLanguage = i18n.language;

            // Fetch translations for each waypoint
            for (const waypointId of waypointIds) {
                try {
                    // Get original waypoint from display data
                    const originalWaypoint = displayData.waypoints.find(wp => wp.waypoint_id === waypointId);
                    const originalName = originalWaypoint?.waypoint_name || originalWaypoint?.name || '';
                    const originalBoardingInfo = originalWaypoint?.boarding_location_info || '';

                    // Initialize translation object for this waypoint
                    translations[waypointId] = {
                        name: null,
                        boarding_location_info: null
                    };

                    // Fetch name translation
                    if (originalName) {
                        const translatedContent = await translationService.getTranslatedContent(
                            entityId,
                            'waypoint',
                            waypointId,
                            'name',
                            userLanguage,
                            originalName
                        );

                        translations[waypointId].name = translatedContent;

                        // If translation failed for user language and it's different from default, try entity default
                        if (!translatedContent.isTranslated && userLanguage !== defaultLanguage) {
                            const fallbackTranslation = await translationService.getTranslatedContent(
                                entityId,
                                'waypoint',
                                waypointId,
                                'name',
                                defaultLanguage,
                                originalName
                            );

                            if (fallbackTranslation.isTranslated) {
                                translations[waypointId].name = fallbackTranslation;
                            }
                        }
                    }

                    // Fetch boarding_location_info translation
                    if (originalBoardingInfo) {
                        const boardingInfoTranslation = await translationService.getTranslatedContent(
                            entityId,
                            'waypoint',
                            waypointId,
                            'boarding_location_info',
                            userLanguage,
                            originalBoardingInfo
                        );

                        translations[waypointId].boarding_location_info = boardingInfoTranslation;

                        // If translation failed for user language and it's different from default, try entity default
                        if (!boardingInfoTranslation.isTranslated && userLanguage !== defaultLanguage) {
                            const fallbackBoardingInfo = await translationService.getTranslatedContent(
                                entityId,
                                'waypoint',
                                waypointId,
                                'boarding_location_info',
                                defaultLanguage,
                                originalBoardingInfo
                            );

                            if (fallbackBoardingInfo.isTranslated) {
                                translations[waypointId].boarding_location_info = fallbackBoardingInfo;
                            }
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching translation for waypoint ${waypointId}:`, error);
                }
            }

            setWaypointTranslations(translations);
        };

        fetchWaypointTranslations();
    }, [
        displayData?.waypoints,
        entitySettings?.multi_language_enabled,
        currentEntity?.entityId,
        currentEntity?.id,
        i18n.language,
        entitySettings?.entity_default_language,
        currentEntity?.entity_default_language
    ]);

    return waypointTranslations;
};

export default useWaypointTranslations;
